import React, { useEffect, useState } from 'react';

import Box from '@amzn/meridian/box';
import { type BoxProps } from '@amzn/meridian/box/box';

const BREAKPOINTS = {
  Xs: 500,
  Sm: 720,
  Md: 1024,
  Lg: 1280,
  Xl: 1600,
} as const;

export interface ResponsiveWidthBoxProps extends BoxProps {
  xl?: PropValue;
  lg?: PropValue;
  md?: PropValue;
  sm?: PropValue;
  xs?: PropValue;
}

type ValsObj = { width?: string; spacingInset?: string };
type PropValue = number | string | undefined | { width?: string | number; spacingInset?: BoxProps['spacingInset'] };

/**
 * Size a Box based on the browser width. This is meant to provide similar features to how Material UI
 * containers work. Note that if you define the width for a lesser breakpoint and not the greater point,
 * the lesser breakpoint will be used for all values greater. Width is the default property but you can
 * specify the width and/or spacingInset
 * Each property can be: "string | number | { width?: string | number; spacingInset?: BoxProps['spacingInset'] }
 * Properties are: "xs", "sm", "md", "lg", "xl"
 */
const ResponsiveWidthBox = (props: ResponsiveWidthBoxProps) => {
  const { width: widthProps, spacingInset: spacingInsetProps, xl, lg, md, sm, xs, children, ...otherProps } = props;

  // State
  const [width, setWidth] = useState<string | undefined>(numToStr(widthProps));
  const [spacingInset, setSpacingInset] = useState<BoxProps['spacingInset']>(spacingInsetProps);

  useEffect(() => {
    function handleResize() {
      let valsObj: ValsObj = {};
      if (window.innerWidth >= BREAKPOINTS.Xl) {
        valsObj = getFirstDefinedValue([xl, lg, md, sm, xs]);
      } else if (window.innerWidth >= BREAKPOINTS.Lg) {
        valsObj = getFirstDefinedValue([lg, md, sm, xs]);
      } else if (window.innerWidth >= BREAKPOINTS.Md) {
        valsObj = getFirstDefinedValue([md, sm, xs]);
      } else if (window.innerWidth >= BREAKPOINTS.Sm) {
        valsObj = getFirstDefinedValue([sm, xs]);
      } else if (window.innerWidth >= BREAKPOINTS.Xs) {
        valsObj = getFirstDefinedValue([xs]);
      }
      if (valsObj.width !== undefined && valsObj.width !== width) {
        setWidth(valsObj.width);
      }
      if (valsObj.spacingInset !== undefined && valsObj.spacingInset !== spacingInset) {
        setSpacingInset(valsObj.spacingInset);
      }
    }
    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial size
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Box width={width} spacingInset={spacingInset} {...otherProps}>
      {children}
    </Box>
  );
};

/**
 * Get the highest defined value for an array of values
 */
const getFirstDefinedValue = (values: PropValue[]): ValsObj => {
  const firstDefined = values.find((val) => val !== undefined);
  // Undefined
  if (!firstDefined) {
    return {};
  }
  // String/number
  if (typeof firstDefined === 'number') {
    return { width: `${firstDefined}px` };
  } else if (typeof firstDefined === 'string') {
    return { width: firstDefined };
  }
  // Object
  const retVal: ValsObj = {};
  if (firstDefined.width !== undefined) {
    retVal.width = numToStr(firstDefined.width);
  }
  if (firstDefined.spacingInset !== undefined) {
    retVal.spacingInset = firstDefined.spacingInset;
  }
  return retVal;
};

/**
 * Convert a width number or string to just use a string
 */
const numToStr = (val: number | string | undefined): string | undefined => {
  if (typeof val === 'number') {
    return val + 'px';
  } else {
    return val;
  }
};

export default ResponsiveWidthBox;
